import { useCallback, useState } from 'react';
import { useMutation } from 'react-query';
import { useDeepCompareEffect } from 'react-use';

import { AddressInfoDto } from '@savgroup-front-common/types';
import { CarrierService } from '@/api';

const useSuggestedAddress = ({
  currentAddress,
}: {
  currentAddress: AddressInfoDto;
}) => {
  const [isSuggestAddressOpen, setIsSuggestAddressOpen] = useState(false);
  const [isSuggestedAddressSelected, setIsSuggestedAddressSelected] = useState<
    boolean | undefined
  >(undefined);

  const { data: suggestedAddressData, mutateAsync: handleValidateAddress } =
    useMutation([], async (newAddress: AddressInfoDto | undefined) => {
      if (!newAddress) {
        return undefined;
      }

      const response = await CarrierService.getVerifiedAddress({
        address: {
          street1: newAddress?.address,
          zipCode: newAddress?.postalCode,
          state: newAddress?.city,
          country: newAddress?.countryCode,
          contactLastName: newAddress?.lastname || '',
          contactFirstName: newAddress?.firstname || '',
          phone: newAddress?.phone || '',
          additionalInformation: newAddress?.additionalInformation || '',
          hasElevator: newAddress?.hasElevator || false,
          housingType: newAddress?.housingType || '',
          hasParkingSpace: newAddress?.hasParkingSpace || false,
          countryCode: newAddress?.countryCode,
          city: newAddress?.city,
        },
      });

      if (response.failure) {
        return undefined;
      }

      const isSuggestedAddressEqualToCurrentAddress =
        response.value.street1.toLowerCase() ===
          newAddress?.address.toLowerCase() &&
        response.value.zipCode.toLowerCase() ===
          newAddress?.postalCode.toLowerCase() &&
        response.value.city.toLowerCase() === newAddress?.city.toLowerCase();

      if (!isSuggestedAddressEqualToCurrentAddress) {
        return {
          ...newAddress,
          address: response.value.street1,
          city: response.value.city,
          postalCode: response.value.zipCode,
          countryCode: response.value.countryCode,
          additionalAddress: response.value.street2,
          phone: response.value.phone,
          additionalInformation: response.value.additionalInformation,
          hasElevator: response.value.hasElevator,
          housingType: response.value.housingType,
          hasParkingSpace: response.value.hasParkingSpace,
          doorCode: response.value.doorCode,
          floor: response.value.floor,
          firstname: response.value.contactFirstName,
          lastname: response.value.contactLastName,
          companyName: response.value.companyName,
          email: response.value.email,
          civility: response.value.civility,
          country: response.value.country,
        } as AddressInfoDto;
      }

      return undefined;
    });

  const handleCompareAddress = useCallback(
    (suggestedAddress: any, addressForm: any) => {
      if (suggestedAddress && addressForm) {
        const hasSameAddress =
          suggestedAddress?.address === addressForm?.address;
        const hasSamePostalCode =
          suggestedAddress?.postalCode === addressForm?.postalCode;
        const hasSameCountryCode =
          suggestedAddress?.countryCode === addressForm?.countryCode;
        const hasSameCity = suggestedAddress?.city === addressForm?.city;

        return Boolean(
          hasSameAddress &&
          hasSamePostalCode &&
          hasSameCountryCode &&
          hasSameCity,
        );
      }

      return true;
    },
    [],
  );

  const handleVerifyAddress = async ({
    addressSelected,
  }: {
    addressSelected: AddressInfoDto;
  }) => {
    const suggestedAddressData = await handleValidateAddress(addressSelected);

    const resultHasSameAddress = handleCompareAddress(
      suggestedAddressData,
      addressSelected,
    );

    if (!resultHasSameAddress) {
      setIsSuggestAddressOpen(true);
      setIsSuggestedAddressSelected(true);
    }

    return resultHasSameAddress;
  };

  useDeepCompareEffect(() => {
    setIsSuggestAddressOpen(false);
    setIsSuggestedAddressSelected(undefined);
  }, [
    currentAddress?.address,
    currentAddress?.postalCode,
    currentAddress?.city,
    currentAddress?.countryCode,
  ]);

  const onChooseAddress = useCallback((isSuggestedAddressSelected: boolean) => {
    setIsSuggestedAddressSelected(isSuggestedAddressSelected);
  }, []);

  return {
    isSuggestedAddressSelected,
    suggestedAddressData,
    isSuggestAddressOpen,
    handleVerifyAddress,
    onChooseAddress,
  };
};

export default useSuggestedAddress;
